import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';
import { setupAxiosInterceptor, resetSessionTimeoutState } from '../../services/axiosInterceptor';

/**
 * SessionTimeout Context Interface
 */
export interface SessionTimeoutContextType {
  /** Whether the session timeout modal is visible */
  showSessionTimeoutModal: boolean;
  /** Trigger the session timeout modal */
  triggerSessionTimeout: () => void;
  /** Close the session timeout modal and handle logout */
  handleSessionTimeoutLogin: () => void;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined);

/**
 * Hook to use SessionTimeout context
 */
export const useSessionTimeout = () => {
  const context = useContext(SessionTimeoutContext);
  if (context === undefined) {
    throw new Error('useSessionTimeout must be used within a SessionTimeoutProvider');
  }
  return context;
};

interface SessionTimeoutProviderProps {
  children: ReactNode;
}

/**
 * SessionTimeoutProvider
 * 
 * Manages session timeout state and integrates with:
 * - Axios interceptor for error detection
 * - Auth context for logout functionality
 * - Router navigation for redirect to login
 */
export const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({ children }) => {
  console.log('[SessionTimeout Context] Provider rendering...');
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  console.log('[SessionTimeout Context] Hooks initialized');

  /**
   * Trigger the session timeout modal
   * Called by axios interceptor when 403/401 is detected
   */
  const triggerSessionTimeout = useCallback(() => {
    console.log('[SessionTimeout Context] Triggering session timeout modal');
    setShowSessionTimeoutModal(true);
  }, []);

  /**
   * Handle login button click in modal
   * Logs out user and redirects to login page
   */
  const handleSessionTimeoutLogin = useCallback(() => {
    console.log('[SessionTimeout Context] Handling session timeout login');
    
    // Close modal
    setShowSessionTimeoutModal(false);
    
    // Logout user (clears tokens and user data)
    logout();
    
    // Reset interceptor state
    resetSessionTimeoutState();
    
    // Redirect to login page
    setTimeout(() => {
      console.log('[SessionTimeout Context] Navigating to login page');
      window.location.href = '/login-page';
      //navigate('/login-page');
    }, 1000);
  }, [logout, navigate]);

  /**
   * Setup axios interceptor on mount
   */
  useEffect(() => {
    console.log('[SessionTimeout Context] useEffect running - Setting up axios interceptor');
    setupAxiosInterceptor(triggerSessionTimeout);
    console.log('[SessionTimeout Context] Interceptor setup complete');
    
    return () => {
      // Cleanup on unmount
      console.log('[SessionTimeout Context] Cleaning up on unmount');
      resetSessionTimeoutState();
    };
  }, [triggerSessionTimeout]);

  const value: SessionTimeoutContextType = {
    showSessionTimeoutModal,
    triggerSessionTimeout,
    handleSessionTimeoutLogin,
  };

  return (
    <SessionTimeoutContext.Provider value={value}>
      {children}
    </SessionTimeoutContext.Provider>
  );
};
